import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Image,
  Link,
  Flex,
  Heading,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import team from "../assets/team.avif";
import world_class from "../assets/world-class-dental-care.avif";
import { FaStar } from "react-icons/fa";
import dental1 from "../assets/dental-1.avif";
import dental2 from "../assets/dental-2.avif";
import dental4 from "../assets/dental-4.avif";
import dental5 from "../assets/dental-5.avif";

// Descriptive alt text per slide — image filenames tell a screen reader (or an
// image crawler) nothing.
const heroSlides = [
  { src: dental5, alt: "Patient receiving dental care at Qualiteeth Dental Care, Jaipur" },
  { src: dental2, alt: "Smiling patient after dental treatment in Vaishali Nagar, Jaipur" },
  { src: dental1, alt: "Dentist checking a patient's teeth at Qualiteeth Dental Care" },
  { src: dental4, alt: "Healthy smile after professional teeth cleaning at Qualiteeth Dental Care" },
];

const HeroSection = () => {
  const [showVideo] = useState(false);
  const images = heroSlides;

  const [currentIndex, setCurrentIndex] = useState(0);

  // Warm the slides the carousel is about to show. Without this each rotation
  // starts a fresh download, and because the browser keeps reassigning Largest
  // Contentful Paint to whatever just painted, LCP was landing on slide 3 with
  // ~8s of load delay. Fetching them up front keeps LCP on the first slide.
  useEffect(() => {
    const timer = setTimeout(() => {
      heroSlides.slice(1).forEach((slide) => {
        // createElement, not `new Image()` — `Image` in this module is Chakra's
        // component, not the DOM constructor.
        const img = document.createElement("img");
        img.src = slide.src;
      });
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Hold on the first slide while prerendering, so the captured HTML shows
    // the same slide the client renders first.
    if (typeof window !== "undefined" && window.__PRERENDER__) return undefined;

    // Advance via the updater form so the interval is created once, rather than
    // being torn down and rebuilt on every slide change.
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev === heroSlides.length - 1 ? 0 : prev + 1));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Box
        id="home"
        position="relative"
        height={{ base: "max-content", md: "90vh" }}
        overflow="hidden"
        display="flex"
        alignItems="center"
        // border="1px solid black"
        backgroundColor={!showVideo ? "white" : "transparent"}
        px={{ base: 0, md: 8 }}
        marginTop={{ base: "80px", md: "80px" }}
      >
        <Flex
          width="100%"
          height={{ base: "auto", md: "60vh" }}
          alignItems="center"
          justifyContent="center"
          flexDirection={{ base: "column", md: "row" }} // Mobile: Column, Desktop: Row
          paddingTop={{ base: 5 }}
          gap={{ md: 4, base: 5 }}
        >
          {/* Text Section */}
          <VStack
            spacing={{ base: 3, md: 4 }}
            align={{ base: "center", md: "flex-start" }}
            maxW={{ base: "90%", md: "600px" }}
            textAlign={{ base: "center", md: "left" }}
          >
            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "5xl" }}
              fontWeight="light"
              lineHeight="1.2"
            >
              Say Goodbye to Teeth Issues
            </Heading>
            <Heading
              as="h2"
              fontSize={{ base: "xl", md: "4xl" }}
              fontWeight="light"
              lineHeight="1.2"
            >
              Embrace a Healthier Smile
            </Heading>

            <Box display={{ base: "none", md: "block" }}>
              <Link href="tel:+919256491206">
                <Button
                  bg="#28211d"
                  color="#fceee5"
                  size="lg"
                  _hover={{ color: "#c49d84" }}
                  name="Call Now"
                >
                  Call Now
                </Button>
              </Link>
            </Box>
          </VStack>

          {/* Image Section */}
          <Box
            position="relative"
            width={{ base: "100%", md: "50%" }}
            overflow="hidden"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Image
              src={images[currentIndex].src}
              alt={images[currentIndex].alt}
              maxW="100%"
              height="400px"
              objectFit="cover"
              transition="opacity 0.5s"
              boxShadow="lg"
              fetchpriority="high"
            />
          </Box>

          {/* Call Now Button - Separate for Mobile */}
          <Box
            display={{ base: "flex", md: "none" }}
            justifyContent="center"
            marginBottom={5}
          >
            <Link href="tel:+919256491206">
              <Button
                name="Call Now"
                bg="#28211d"
                color="#fceee5"
                size="lg"
                _hover={{ color: "#c49d84" }}
              >
                Call Now
              </Button>
            </Link>
          </Box>

          {/* Call Now Button - For Desktop */}
        </Flex>
      </Box>
      <Box
        bg="#f5d5bc" // Background color
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={{ base: 0, md: 8 }}
        py={{ base: 16, md: 16 }}
        id="about"
      >
        <Box
          bg="#f5d5bc"
          border="1px solid rgba(0, 0, 0, 0.2)"
          borderRadius="10px"
          p={{ base: 6, md: 14 }}
          maxW="1000px"
          w="90%"
          display="flex"
          flexDirection={{ base: "column", md: "row" }}
          alignItems="center"
          gap={8}
        >
          {/* Left Content Section */}
          <VStack align="start" flex="1" spacing={6}>
            <Heading
              as="h2"
              fontSize={{ base: "xl", md: "4xl" }}
              fontWeight="bold"
              color="black"
              lineHeight="1.2"
            >
              Welcome to Qualiteeth – Dental Clinic in Jaipur
            </Heading>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="black"
              fontWeight="light"
              lineHeight="1.2"
            >
              Experience world-class dental care in Jaipur at Qualiteeth Dental
              Clinic, led by<strong> Dr. Aishwarya Sarda</strong>, an experienced MDS in
              Conservative Dentistry and Endodontics. Conveniently located in
              Vaishali Nagar, we provide painless dental treatments with
              cutting-edge technology in a modern and comfortable setting.
            </Text>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="black"
              fontWeight="light"
              lineHeight="1.2"
            >
              Whether you’re searching for a "dental clinic near me", expert
              teeth cleaning, teeth whitening, or root canal treatments, we are
              your go-to destination for trusted dental care.
            </Text>

            <VStack align="start" spacing={2}>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="black"
                fontWeight="light"
                lineHeight="1.2"
              >
                Book your appointment today and discover the Qualiteeth
                difference! 😁✨
              </Text>
              <HStack align="start">
                <FaStar size={14} color="black" style={{ marginTop: "5px" }} />
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="black"
                  fontWeight="light"
                >
                  8+ Years of experience
                </Text>
              </HStack>
              <HStack align="start">
                <FaStar size={14} color="black" style={{ marginTop: "5px" }} />
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="black"
                  fontWeight="light"
                >
                  Experienced Endodontist
                </Text>
              </HStack>
              <HStack align="start">
                <FaStar size={14} color="black" style={{ marginTop: "5px" }} />
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="black"
                  fontWeight="light"
                >
                  Comprehensive Treatment Solutions
                </Text>
              </HStack>
              <HStack align="start">
                <FaStar size={14} color="black" style={{ marginTop: "5px" }} />
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="black"
                  fontWeight="light"
                >
                  #1 Dental Clinic by reviews 
                </Text>
              </HStack>
              <HStack align="start">
                <FaStar size={14} color="black" style={{ marginTop: "5px" }} />
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="black"
                  fontWeight="light"
                >
                  Founder of Qualiteeth Dental Care
                </Text>
              </HStack>
              <HStack align="start">
                <FaStar size={14} color="black" style={{ marginTop: "5px" }} />
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="black"
                  fontWeight="light"
                >
                  Commitment to Continuous Improvement
                </Text>
              </HStack>
            </VStack>
          </VStack>

          {/* Right Image Section */}
          <Box flex="1">
            <Image
              src={team}
              alt="Dr. Aishwarya Sarda at Qualiteeth Dental Care, Vaishali Nagar, Jaipur"
              width="100%"
              height="100%"
              objectFit="cover"
              loading="lazy"
            />
            <Image
              src={world_class}
              alt="World-class dental care badge"
              loading="lazy"
              top={{ base: "-40px", md: "-50px" }}
              left={{ base: "200px", md: "250px" }}
              position="relative"
              width={{ md: "150px", base: "100px" }}
              height={{ md: "150px", base: "100px" }}
              objectFit="contain"
              transition="transform 0.1s linear"
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default HeroSection;
